import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Menu from "../components/menu";
import { useTranslation } from "react-i18next";
import { Store } from "../store";
import useModal from "../hooks/useModal";
import Home from "./home";
import useResponsive from "../hooks/useResponsive";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";

const Header = () => {
  const { config, checkout } = Store.useState(s => s);
  const location = useLocation();
  const { Modal, useModalComponent } = useModal();
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useResponsive({ maxWidth: config.eshop.theme.selected.settings?.mobileBreakpoint });
  const [isSearching, setIsSearching] = useState(false);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const icon = config.modules.find(m => m.id === "designs").data.squareSVG;

  const handleToggle = () => {
    setIsSearching(!isSearching);
  };

  useEffect(() => {
    setIsOpen(false);
    setIsSearching(false);
  }, [location.pathname]);

  const settings = config.eshop?.theme?.selected?.settings;

  useEffect(() => {
    if (isOpen) showModal();
    else hideModal();
  }, [isOpen]);

  const styles = config.getModalStyles();
  if (isMobile) {
    styles.content.backgroundColor = "#fff";
    styles.content.width = "95vw";
    styles.content.height = "80vh";
    styles.content.padding = "0px";
    styles.content.borderRadius = "20px";
  } else {
    styles.content.backgroundColor = "#fff";
    styles.content.width = "70vw";
    styles.content.height = "70vh";
    styles.content.padding = "0px";
    styles.content.borderRadius = "20px";
  }

  const [showModal, hideModal] = useModalComponent(
    () => (
      <Modal ariaHideApp={false} isOpen={isOpen} onRequestClose={e => setIsOpen(false)} style={styles}>
        <Home isMobile={isMobile}/>
      </Modal>
    ),
    [isOpen, isMobile]
  );

  const hasCheckoutItem = checkout.items.length > 0;

  if (isMobile)
    return <div id="header" className={`${settings?.header?.layout?.id || "centered"} mobile`}>
      <MobileMenu config={config} isOpen={mobileMenuIsOpen} setIsOpen={setMobileMenuIsOpen} location={location} />
      <div className={"content"}>
        <button type="button" className="burger" onClick={e => setMobileMenuIsOpen(true)}>
          <i className="cg-icon-burger" />
        </button>
        {icon ? <div className="logo"><Link to="/"><img style={{ minWidth: settings.logo?.width || 80, maxWidth: settings.logo?.width || 80, height: "auto" }} src={icon} /></Link></div> : <span/>}
        <div className="profileAndBasket">
          <button onClick={handleToggle}><i className="cg-icon-search" /></button>
          <button onClick={e => setIsOpen(true)}><i className={`cg-icon-basket-${hasCheckoutItem ? "selected" : "cart"}`} /></button>
        </div>
      </div>
      <Search isMobile handleToggle={handleToggle} isSearching={isSearching} setIsSearching={setIsSearching} />
    </div>;

  return <div id="header" className={`${settings?.header?.layout?.id || "centered"}`}>
    <div className={"content"}>
      <Menu location="header" />
      {icon ? <div className="logo"><Link to="/"><img style={{ minWidth: settings.logo?.width || 80, maxWidth: settings.logo?.width || 80, height: "auto" }} src={icon} /></Link></div> : <span/>}
      <div className="profileAndBasket">
        <button onClick={handleToggle}><i className="cg-icon-search" /></button>
        <button onClick={e => setIsOpen(true)}><i className={`cg-icon-basket-${hasCheckoutItem ? "selected" : "cart"}`} /></button>
      </div>
    </div>
    <Search handleToggle={handleToggle} isSearching={isSearching} setIsSearching={setIsSearching} />
  </div>;
};

const MobileMenu = ({ config, isOpen, setIsOpen, location }) => {
  const targetRef = React.createRef();
  const settings = config.eshop?.theme?.selected?.settings;

  useEffect(() => {
    if (targetRef.current) {
      if (isOpen) disableBodyScroll(targetRef.current);
      else enableBodyScroll(targetRef.current);
    }
    return () => clearAllBodyScrollLocks();
  }, [targetRef]);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return <div ref={targetRef} id="mobileMenu" className={ isOpen ? "open" : "closed"} style={{ backgroundColor: `var(--${settings.backgroundColour?.id})` }}>
    <div className="header">
      <button onClick={e => setIsOpen(false)}><i className="cg-icon-close" /> </button>
      <span />
    </div>
    <div className="content">
      <Menu location="header" />
      <Menu location="navbar" />
    </div>
  </div>;
};

const Search = ({ isMobile, handleToggle, isSearching, setIsSearching }) => {
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const searchInputRef = useRef();

  const handleSearch = (e) => {
    e.preventDefault();
    if (term.length < 2) return;
    setIsSearching(false);
    setTerm("");
    navigate(`/search?term=${term}`);
  };

  window.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.code === "KeyF") {
      e.preventDefault();
      if (!isSearching && !isMobile) {
        setIsSearching(true);
      }
    }
  });

  useEffect(() => {
    if (isSearching)
      searchInputRef.current.focus();
    else
      searchInputRef.current.blur();
  }, [isSearching]);

  return <div id="search" className={isSearching ? "open" : ""}>
      <form onSubmit={handleSearch} action="/search" method="POST">
        <div className="left">
          <i className="cg-icon-search" />
          <input required type="text" ref={searchInputRef} autoCapitalize="none" autoFocus={true} placeholder={t("searchPlaceholder")} autoComplete="off" value={term} onChange={e => setTerm(e.target.value)} name="term" />
        </div>
        <button type="button" onClick={handleToggle}> <i className="cg-icon-close" /></button>
      </form>
    </div>;
};

export default Header;
